import find from 'lodash/find';
import first from 'lodash/first';

export enum SupportedLocale {
    enUS = 'en-US',
    esUS = 'es-US',
    frCA = 'fr-CA',
    frFR = 'fr-FR',
}

export interface PathParseResult {
    locale: SupportedLocale | undefined;
    rest: string;
}

export const STORAGE_KEY = {
    OVERRIDE: 'ovations-locale.override',
};

export const DEFAULT_LOCALE = SupportedLocale.enUS;

export const normalizeLocale = (locale = ''): SupportedLocale | undefined => {
    const localeLowerCased = locale.toLowerCase();
    const match = find(SupportedLocale, (value) => value.toLowerCase() === localeLowerCased);
    return match;
};

const ensureLeadingSlash = (path: string): string => {
    if (path[0] === '/') {
        return path;
    }
    return `/${path}`;
};

export const parsePath = (path: string): PathParseResult => {
    const normalizedPath = ensureLeadingSlash(path);
    const parts = normalizedPath.split('/');
    const normalizedLocale = normalizeLocale(parts[1]);
    if (normalizedLocale) {
        return { locale: normalizedLocale, rest: ensureLeadingSlash(parts.slice(2).join('/')) };
    }
    return { locale: undefined, rest: normalizedPath };
};

export const getPath = (result: PathParseResult): string => {
    if (!result.locale || result.locale === DEFAULT_LOCALE) {
        return result.rest;
    }
    return `/${result.locale}${result.rest}`;
};

// acceptLanguage is the value of the browser's Accept-Language request header
// Normally we'd use navigator.language, but IE11 reports it incorrectly in certain versions of windows
export const getUserLocale = (acceptLanguage: string[]): SupportedLocale => {
    const locale = sessionStorage.getItem(STORAGE_KEY.OVERRIDE) || first(acceptLanguage) || DEFAULT_LOCALE;
    return normalizeLocale(locale) || DEFAULT_LOCALE;
};

export const setLocaleOverride = (locale: SupportedLocale) => {
    sessionStorage.setItem(STORAGE_KEY.OVERRIDE, locale);
};
